import { upsertLocally } from '@paragon/client-tools';
import { CeilingTray } from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import { enqueueError } from '@paragon/notification-tools';
import { addSpinnerMessage, removeSpinnerMessage } from '@paragon/task-spinner';
import { forceArray } from '@paragon/utilities';

import { isInteractiveEditActive } from '../../selectors/interactiveEditingSelectors';
import {
  getActiveLayoutLevelGuidOrNull,
  getAllCeilingTraysDictionary,
  getCeilingTrayElevationFromActiveLevel,
} from '../../selectors/layoutDataSelectors';
import { applicationOnInsertingCeilingTray, isInsertionCeilingTrayFullyDefined } from '../../slices/applicationSlice';
import { layoutSlice } from '../../slices/layoutSlice';
import { ensureLevelIsVisible } from '../levelVisibilityActions';
import { selectItem } from '../selectActions';
import { AsyncThunkActionVoid, ThunkActionVoid } from '../thunkActions';

export const startInsertingCeilingTray = (): ThunkActionVoid => (dispatch, getState) => {
  const state = getState();

  const levelGuid = getActiveLayoutLevelGuidOrNull(state);
  const elevationsFromLevel = getCeilingTrayElevationFromActiveLevel(state);

  if (levelGuid) dispatch(ensureLevelIsVisible(levelGuid, { exclusive: true }));

  dispatch(applicationOnInsertingCeilingTray({ levelGuid, ...elevationsFromLevel }));
};

export const finishInsertingCeilingTray = (): AsyncThunkActionVoid => async (dispatch, getState) => {
  const state = getState();
  const { insertingObjectInfo } = state.application;

  if (insertingObjectInfo?.type !== 'ceilingTray') return;
  if (!isInsertionCeilingTrayFullyDefined(insertingObjectInfo.data)) return;

  const createdTray = { ...insertingObjectInfo.data, guid: crypto.randomUUID() } satisfies CeilingTray;
  dispatch(layoutSlice.actions.ceilingTrayCreated(createdTray));
  dispatch(selectItem(createdTray, 'ceilingTray'));

  await dispatch(saveCeilingTrays());
};

export interface CeilingTrayEditInstructions {
  guid: string;
  editedProperties: Partial<Omit<CeilingTray, 'guid'>>;
}

export const editCeilingTrays =
  (instructions: CeilingTrayEditInstructions[], options?: { createdTrays: CeilingTray[] }): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const state = getState();

    let existingTrays = getAllCeilingTraysDictionary(state);
    if (options?.createdTrays) {
      existingTrays = upsertLocally(existingTrays, options.createdTrays);
    }

    const editedTrays = forceArray(instructions).map(({ guid, editedProperties }) => {
      const targetTray = existingTrays[guid];
      if (targetTray == null) {
        dispatch(enqueueError('Unable to edit ceiling tray. Please contact Paragon Customer Support.'));
        throw new Error(`Unable to find ceiling tray '${guid}' in state to edit.`);
      }

      return { ...targetTray, ...editedProperties };
    });

    dispatch(layoutSlice.actions.ceilingTraysUpserted(editedTrays));
    await dispatch(saveCeilingTrays());
  };

export const saveCeilingTrays = (): AsyncThunkActionVoid => async (dispatch, getState) => {
  const state = getState();
  if (state.application.openContext?.type !== 'project') return;

  const spinnerId = dispatch(addSpinnerMessage('Saving ceiling trays...'));

  try {
    await designService.projects.replaceCeilingTrays(
      state.application.openContext.projectGuid,
      state.layout.ceilingTrays,
    );
  } catch (error) {
    console.error(error);
    dispatch(enqueueError('Unable to save ceiling trays. Please try again or contact Paragon Customer Support.'));
  } finally {
    dispatch(removeSpinnerMessage(spinnerId));
  }
};

export const setHoveredTrayStateIfApplicable =
  (trayGuid: string): ThunkActionVoid =>
  (dispatch, getState) => {
    const state = getState();
    if (isInteractiveEditActive(state)) return;

    if (state.layout.hoveredRoofFace?.type !== 'ceilingTrayPolygon' || state.layout.hoveredRoofFace.guid !== trayGuid) {
      dispatch(layoutSlice.actions.hoveredCeilingTraySet(trayGuid));
    }
  };

export const clearHoveredTrayStatesIfActive =
  (trayGuid: string): ThunkActionVoid =>
  (dispatch, getState) => {
    const state = getState();
    if (isInteractiveEditActive(state)) return;

    if (state.layout.hoveredRoofFace?.type === 'ceilingTrayPolygon' && state.layout.hoveredRoofFace.guid === trayGuid) {
      dispatch(layoutSlice.actions.hoveredRoofFaceCleared());
    }
  };
